// ----------      START   IMPORT  BLOCK        ----------
import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import EnumSelect from "./Widgets/EnumSelect";
// Context
import { useLanguage } from "../../../../../API/Context/LanguageContext";
// Config
import { VocabProfileOnboarding as Vocab } from "../../../../../ConfigFiles/multilingual/VocabProfileOnboarding";
import { BLOOD_TYPES } from "../../../../../ConfigFiles/maps/blood_types_map";
import { EDUCATION_CONFIG } from "../../../../../ConfigFiles/maps/education_map";
import { MARITAL_STATUS } from "../../../../../ConfigFiles/maps/marital_status_map";
import { RELIGION_CONFIG } from "../../../../../ConfigFiles/maps/religion_map";
import { SEX_CONFIG } from "../../../../../ConfigFiles/maps/sex_map";
import { BloodTypesEnum } from "../../../../../Types/Enums/BloodTypesEnum";
// ----------      END   IMPORT  BLOCK        ----------

export interface BasicsFormData {
  birthday?: string | null;
  birthplace?: string | null;
  blood_type?: BloodTypesEnum | string | null;
  sex?: number | string | null;
  height?: number | null;
  weight?: number | null;
  marital_status?: number | string | null;
  scholarship?: number | string | null;
  religion?: number | string | null;
  occupation?: string | null;
}

interface StepBasicsProps {
  data: BasicsFormData;
  update: (newData: Partial<BasicsFormData>) => void;
  isImmutable?: boolean;
  no_core?: boolean;
}

const StepBasics: React.FC<StepBasicsProps> = ({
  data,
  update,
  isImmutable = false,
  no_core = false,
}) => {
  const { lan } = useLanguage();

  const isInvalidText = (value?: string | null): boolean => {
    if (typeof value === "string") return value.trim() === "";
    return value === null || value === undefined;
  };

  const isInvalidEnum = (val?: unknown): boolean =>
    val === null || val === undefined || val === "";

  return (
    <div className="animate-in">
      <h4 className="mb-4 border-start border-primary border-4 ps-3">
        {Vocab.basics_title[lan]}
      </h4>

      <Row className="g-3">
        {/* --- SECTION: CORE IDENTITY (Hidden if no_core is true) --- */}
        {!no_core && (
          <>
            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.birthday[lan]}</Form.Label>
              <Form.Control
                type="date"
                isInvalid={isInvalidText(data.birthday)}
                value={data.birthday || ""}
                onChange={(e) => update({ birthday: e.target.value })}
              />
            </Col>

            <Col md={8}>
              <Form.Label className="small fw-bold">{Vocab.birthplace[lan]}</Form.Label>
              <Form.Control
                type="text"
                isInvalid={isInvalidText(data.birthplace)}
                value={data.birthplace || ""}
                onChange={(e) => update({ birthplace: e.target.value })}
                placeholder="City, State, Country"
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.blood_type[lan]}</Form.Label>
              <EnumSelect
                value={data.blood_type ?? ""}
                options={BLOOD_TYPES}
                isInvalid={isInvalidEnum(data.blood_type)}
                onChange={(val: any) => update({ blood_type: val })}
              />
            </Col>
          </>
        )}

        {/* Current Sex Field */}
        <Col md={no_core ? 12 : 6}>
          <Form.Label className="small fw-bold">{Vocab.current_sex[lan]}</Form.Label>
          <EnumSelect
            value={data.sex ?? ""}
            options={SEX_CONFIG}
            isInvalid={isInvalidEnum(data.sex)}
            onChange={(val: any) => update({ sex: val })}
          />
        </Col>

        {/* --- SECTION: EXTENDED INFO (Hidden if isImmutable is true) --- */}
        {!isImmutable && (
          <>
            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.height[lan]}</Form.Label>
              <Form.Control
                type="number"
                isInvalid={Boolean(data.height && data.height <= 30)}
                value={data.height ?? ""}
                onChange={(e) => update({ height: parseInt(e.target.value, 10) || 0 })}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.weight[lan]}</Form.Label>
              <Form.Control
                type="number"
                value={data.weight ?? ""}
                onChange={(e) => update({ weight: parseInt(e.target.value, 10) || 0 })}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.civil_status[lan]}</Form.Label>
              <EnumSelect
                value={data.marital_status ?? ""}
                options={MARITAL_STATUS}
                isInvalid={isInvalidEnum(data.marital_status)}
                onChange={(val: any) => update({ marital_status: val })}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.education[lan]}</Form.Label>
              <EnumSelect
                value={data.scholarship ?? ""}
                options={EDUCATION_CONFIG}
                isInvalid={isInvalidEnum(data.scholarship)}
                onChange={(val: any) => update({ scholarship: val })}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold">{Vocab.religion[lan]}</Form.Label>
              <EnumSelect
                value={data.religion ?? ""}
                options={RELIGION_CONFIG}
                isInvalid={isInvalidEnum(data.religion)}
                onChange={(val: any) => update({ religion: val })}
              />
            </Col>

            <Col md={12}>
              <Form.Label className="small fw-bold">{Vocab.occupation[lan]}</Form.Label>
              <Form.Control
                type="text"
                value={data.occupation || ""}
                isInvalid={isInvalidText(data.occupation)}
                onChange={(e) => update({ occupation: e.target.value })}
                placeholder="e.g. Architect"
              />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default StepBasics;